import pdfMake from "pdfmake";
import {
    Document,
    Packer,
    Paragraph,
    TextRun,
    HeadingLevel,
    Table,
    TableRow,
    TableCell,
    WidthType,
    AlignmentType,
    Media,
    ImageRun,
} from "docx";
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import { Node } from "unist";
import { saveAs } from "file-saver";
import vfs from "./fonts/vfs_fonts";
import { Canvg } from "canvg";
(pdfMake as any).vfs = vfs;
(pdfMake as any).fonts = {
    NimbusSans: {
        normal: "NimbusSanL-Reg.otf",
        bold: "NimbusSanL-Bol.otf",
        italics: "NimbusSanL-RegIta.otf",
        bolditalics: "NimbusSanL-BolIta.otf"
    }
};

import MainReportPage from "./_assets/MainReportImage-360.svg";
import InvestmentFront from "./_assets/investment-front.svg";
import ReportFront from "./_assets/report-front.svg";


export const groupBySection = (data: any[]) => {
    return data.reduce((acc, item) => {
        if (!acc[item.section]) {
            acc[item.section] = [];
        }
        acc[item.section].push(item);
        return acc;
    }, {} as Record<string, any[]>);
};

const agentImages: Record<string, string> = {
    "customer-360-agent": MainReportPage,
    "investment-diligence-agent": InvestmentFront,
    "report-anything-agent": ReportFront,
    "": MainReportPage
};

const frontPagemapper: Record<string, any> = {
    "customer-360-agent": {
        image: MainReportPage,
        color: "white",
        top: 460 // ~55% of 841.89 A4 height
    },
    "investment-diligence-agent": {
        image: InvestmentFront,
        color: "black",
        top: 550 // ~65% of 841.89 A4 height
    },
    "report-anything-agent": {
        image: ReportFront,
        color: "black",
        top: 550 // ~65% of 841.89 A4 height
    },
    "": {
        image: MainReportPage,
        color: "white",
        top: 460
    },
};

async function getAgentImage(agentName: string) {
    const url = agentImages[agentName.toLowerCase()];
    if (!url) return null;
    return await toDataURL(url); // ✅ Base64 string
}

export async function downloadReportDocx(
    summaryData: any,
    visualsMap: any,
    title: string,
    desc: string,
    agentName: string
) {
    const groupedSummaryData = groupBySection(summaryData);

    // ---- COVER PAGE ----
    const coverChildren: Paragraph[] = [];
    const response = await fetch(agentImages[agentName.toLowerCase()]);
    const svgBlob = await response.blob();
    const arrayBuffer = await svgBlob.arrayBuffer();

    // Cover Image
    coverChildren.push(
        new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
                new ImageRun({
                    data: new Uint8Array(arrayBuffer),
                    type: "svg",
                    transformation: {
                        width: 800.28,
                        height: 1120.89,
                    },
                    fallback: {
                        data: new Uint8Array(), // supply PNG fallback here if available
                        type: "png",
                    },
                }),
            ],
        })
    );

    // ---- MAIN CONTENT ----
    const contentChildren: any[] = [];

    for (const [idx, [sectionName, subsections]] of Array.from(Object.entries(groupedSummaryData).entries())) {
        // section header
        contentChildren.push(
            new Paragraph({
                text: `${idx + 1}. ${sectionName}`,
                heading: HeadingLevel.HEADING_1,
                spacing: { after: 200 },
            })
        );

        for (const [subIdx, s] of Array.from((subsections as any[]).entries())) {
            // subsection header
            contentChildren.push(
                new Paragraph({
                    text: `${idx + 1}.${subIdx + 1} ${s.subsection}`,
                    heading: HeadingLevel.HEADING_2,
                    spacing: { after: 200 },
                })
            );

            // summary
            if (s.summary) {
                const mdParagraphs = markdownToDocx(s.summary);
                contentChildren.push(...mdParagraphs);
            }

            // visuals
            const visuals = visualsMap[`${s.section} > ${s.subsection}`] || [];
            for (const vis of visuals) {
                if (vis.png_bytes) {
                    contentChildren.push(
                        new Paragraph({
                            alignment: AlignmentType.CENTER,
                            children: [
                                new ImageRun({
                                    data: base64ToUint8Array(`data:image/png;base64,${vis.png_bytes}`),
                                    type: "png",
                                    transformation: { width: 700.28, height: 700.89 },
                                }),
                            ],
                        })
                    );
                }

                if (vis.bytes) {
                    const mime = detectImageMime(vis.bytes, vis.ext);
                    if (mime !== "image/gif") {
                        contentChildren.push(
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [
                                    new ImageRun({
                                        data: base64ToUint8Array(`data:${mime};base64,${vis.bytes}`),
                                        type: "png",
                                        transformation: { width: 700.28, height: 700.89 },
                                    }),
                                ],
                            })
                        );
                    }
                }

                if (vis.svg_content) {
                    const pngBytes = await svgToPngBytes(vis.svg_content.replace(
                        /fill\s*=\s*"(#1ac6ff33|#33de7b1a|#ff00001a)"/gi,
                        'fill="transparent"'
                    ), 864, 612);

                    contentChildren.push(
                        new Paragraph({
                            alignment: AlignmentType.CENTER,
                            children: [
                                new ImageRun({
                                    data: pngBytes,
                                    type: "png",
                                    transformation: { width: 700, height: 612 },
                                }),
                            ],
                        })
                    );
                }

                // spacing after each visual
                contentChildren.push(new Paragraph({ text: "", spacing: { after: 300 } }));
            }

            // sources
            const usedSources = s.retrieved_sources?.filter((src: any) => src.used) || [];
            if (usedSources.length) {
                contentChildren.push(
                    new Paragraph({
                        text: "Sources",
                        heading: HeadingLevel.HEADING_3,
                        spacing: { before: 200, after: 100 },
                    })
                );

                const headerRow = new TableRow({
                    children: ["Sno", "Url", "Query", "Reason", "Score"].map(
                        (text) =>
                            new TableCell({
                                children: [new Paragraph({ text })],
                                shading: { fill: "f3f4f6" },
                                margins: { top: 100, bottom: 100, left: 100, right: 100 },
                            })
                    ),
                });

                const bodyRows = usedSources.map(
                    (src: any, i: number) =>
                        new TableRow({
                            children: [
                                String(i + 1),
                                src.url || "N/A",
                                src.query || "N/A",
                                src.reason || "N/A",
                                src.score != null ? String(src.score) : "N/A",
                            ].map((text, idx) => {
                                if (idx === 1 && src.url) {
                                    return new TableCell({
                                        children: [
                                            new Paragraph({
                                                children: [new TextRun({ text, style: "Hyperlink" })],
                                            }),
                                        ],
                                    });
                                }
                                return new TableCell({ children: [new Paragraph(text)] });
                            }),
                        })
                );

                contentChildren.push(
                    new Table({
                        rows: [headerRow, ...bodyRows],
                        width: { size: 100, type: WidthType.PERCENTAGE },
                        alignment: AlignmentType.CENTER,
                    })
                );

                contentChildren.push(new Paragraph({ text: "", spacing: { after: 400 } }));
            }
        }
    }


    // ---- BUILD DOCUMENT ----
    const doc = new Document({
        sections: [
            {
                properties: {
                    page: {
                        margin: { top: 0, right: 0, bottom: 0, left: 0 },
                    },
                    verticalAlign: "center", // centers content vertically
                },
                children: coverChildren,
            },
            {
                properties: {
                    page: {
                        margin: { top: 720, right: 720, bottom: 720, left: 720 }, // ~0.5 inch
                    },
                },
                children: contentChildren,
            },
        ],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, "FullReport.docx");
}


export async function downloadReportPdf(summaryData: any, visualsMap: any, title: string, desc: string, agentName: string) {
    const groupedSummaryData = groupBySection(summaryData);

    // collect promises for all sections/subsections
    const contentPromises: Promise<any>[] = [];

    const agentImageBase64 = await getAgentImage(agentName);

    // get cover image for agent

    if (agentImageBase64) {
        const config = frontPagemapper[agentName] || frontPagemapper[""];

        contentPromises.push(
            Promise.resolve({
                stack: [
                    // Full background
                    {
                        image: agentImageBase64,
                        width: 595.28,
                        height: 841.89,
                        absolutePosition: { x: 0, y: 0 }
                    },

                    // Center overlay text
                    {
                        absolutePosition: { x: 0, y: config.top },
                        width: 595.28,
                        alignment: "center",
                        stack: [
                            { text: title, fontSize: 24, bold: true, margin: [20, 0, 20, 10], color: config.color, lineHeight: 1.2 },
                            { text: desc, fontSize: 28, bold: true, color: config.color, lineHeight: 1.2, margin: [20, 0, 20, 10] }
                        ]
                    },

                    // Bottom text
                    {
                        absolutePosition: { x: 0, y: 720 },
                        width: 595.28,
                        alignment: "center",
                        text: new Date().toLocaleString("en-US", { month: "long", year: "numeric" }),
                        fontSize: 20,
                        bold: true,
                        color: config.color
                    }
                ],
                pageBreak: "after"
            })
        );
    }



    Object.entries(groupedSummaryData).forEach(([sectionName, subsections]: any, idx) => {
        // section header (sync, no promise needed)
        contentPromises.push(
            Promise.resolve({ text: `${idx + 1}. ${sectionName}`, style: "sectionHeader" })
        );

        subsections.forEach((s: any, subIdx: number) => {
            // subsection header
            contentPromises.push(
                Promise.resolve({ text: `${idx + 1}.${subIdx + 1} ${s.subsection}`, style: "subHeader" })
            );

            // summary
            if (s.summary) {
                const parsedBlocks = markdownToPdfmake(s.summary);
                parsedBlocks.forEach((block: any) => {
                    contentPromises.push(Promise.resolve(block));
                });
            }

            // visuals
            const visuals = visualsMap[`${s.section} > ${s.subsection}`] || [];

            visuals.forEach((vis: any) => {
                if (vis.type === "animation" && vis.png_bytes) {
                    contentPromises.push(
                        Promise.resolve({
                            image: `data:image/png;base64,${vis.png_bytes}`,
                            width: 400,
                            margin: [0, 10, 0, 10],
                            alignment: "center"
                        })
                    );
                }
                if (vis.bytes) {
                    const mime = detectImageMime(vis.bytes, vis.ext);
                    if (mime !== "image/gif") {
                        contentPromises.push(
                            Promise.resolve({
                                image: `data:${mime};base64,${vis.bytes}`,
                                width: 400,
                                margin: [0, 10, 0, 10],
                                alignment: "center"
                            })
                        );
                    }
                } else if (vis.svg_content) {
                    contentPromises.push(
                        svgToPngDataUrl(vis.svg_content.replace(
                            /fill\s*=\s*"(#1ac6ff33|#33de7b1a|#ff00001a)"/gi,
                            'fill="transparent"'
                        )).then((dataUrl) => ({
                            image: dataUrl,
                            width: 400,
                            margin: [0, 10, 0, 10],
                            alignment: "center"
                        }))
                    );
                }
            });

            // ✅ add sources if available

            const usedSources = s.retrieved_sources?.filter((src: any) => src.used) || [];

            if (usedSources?.length) {
                // push table title
                contentPromises.push(
                    Promise.resolve({
                        text: "Sources",
                        style: "subHeader",
                        margin: [0, 10, 0, 6],
                    })
                );

                // prepare table body
                const tableBody: any = [
                    [
                        { text: "Sno", bold: true, fillColor: "#f3f4f6", margin: [3, 3, 3, 3] },
                        { text: "Url", bold: true, fillColor: "#f3f4f6", margin: [3, 3, 3, 3] },
                        { text: "Query", bold: true, fillColor: "#f3f4f6", margin: [3, 3, 3, 3] },
                        { text: "Reason", bold: true, fillColor: "#f3f4f6", margin: [3, 3, 3, 3] },
                        { text: "Score", bold: true, fillColor: "#f3f4f6", margin: [3, 3, 3, 3] },
                    ]
                ];

                usedSources.forEach((item: any, index: number) => {
                    tableBody.push([
                        { text: String(index + 1), margin: [3, 3, 3, 3] },
                        {
                            text: item.url || "N/A",
                            link: item.url || undefined,
                            color: item.url ? "blue" : "black",
                            decoration: item.url ? "underline" : undefined,
                            margin: [3, 3, 3, 3],
                            noWrap: false // allow wrapping long URLs
                        },
                        { text: item.query || "N/A", margin: [3, 3, 3, 3], noWrap: false },
                        { text: item.reason || "N/A", margin: [3, 3, 3, 3], noWrap: false },
                        { text: item.score != null ? String(item.score) : "N/A", margin: [3, 3, 3, 3] },
                    ]);
                });

                contentPromises.push(
                    Promise.resolve({
                        table: {
                            headerRows: 1,
                            // Adjust widths: Url smaller, Query & Reason bigger
                            widths: ["5%", "30%", "30%", "25%", "10%"],
                            body: tableBody,
                        },
                        layout: {
                            fillColor: (rowIndex: number) => rowIndex === 0 ? "#f3f4f6" : null,
                            hLineColor: () => "#d1d5db",
                            vLineColor: () => "#d1d5db",
                        },
                        fontSize: 8,
                        margin: [0, 0, 0, 10],
                    })
                );
            }

        });
    });

    // wait for all content items to resolve
    const content = await Promise.all(contentPromises);

    // now build doc
    const docDefinition = {
        pageSize: "A4",
        pageMargins: [40, 60, 40, 60],
        content,
        defaultStyle: { font: "NimbusSans", fontSize: 11 },
        styles: {
            coverTitle: {
                fontSize: 22,
                bold: true,
            },
            coverSubtitle: {
                fontSize: 12,
                italics: true,
                color: "gray",
            },
            sectionHeader: { fontSize: 18, bold: true, lineHeight: 1.5, margin: [0, 10, 0, 5] },
            subHeader: { fontSize: 16, bold: true, lineHeight: 1.4, margin: [0, 5, 4, 2] },
            summaryText: { fontSize: 10, lineHeight: 1.4, color: "#5f6465" },
            ...pdfStyles
        },
    };

    (pdfMake as any).createPdf(docDefinition).download("FullReport.pdf");
}





function getViewBox(svg: string): [number, number, number, number] {
    const viewBoxMatch = svg.match(/viewBox="([^"]+)"/);

    if (viewBoxMatch) {
        const parts = viewBoxMatch[1].split(/\s+/).map(Number);
        const [x = 0, y = 0, w = 800, h = 600] = parts;
        return [x, y, w, h];
    }

    // fallback: width/height attributes
    const widthMatch = svg.match(/width="([\d.]+)"/);
    const heightMatch = svg.match(/height="([\d.]+)"/);

    if (widthMatch && heightMatch) {
        const w = parseFloat(widthMatch[1]);
        const h = parseFloat(heightMatch[1]);
        return [0, 0, w, h];
    }

    // final fallback
    return [0, 0, 800, 600];
}


export async function svgToPngDataUrl(svg: string): Promise<string> {
    const [x, y, width, height] = getViewBox(svg);

    // create canvas with correct dimensions
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas not supported");

    // render
    const v = await Canvg.from(ctx, svg, { ignoreAnimation: true });
    await v.render();

 

    return canvas.toDataURL("image/png");
}




function detectImageMime(base64: string, ext?: string): string {
    if (ext) {
        return `image/${ext}`;
    }
    if (base64.startsWith("iVBORw0KGgo")) return "image/png";
    if (base64.startsWith("/9j/")) return "image/jpeg";
    if (base64.startsWith("R0lGODlh")) return "image/gif";
    return "image/png"; // fallback
}

async function gifLastFrameToPng(base64Gif: string): Promise<string> {
    return new Promise((resolve, reject) => {
        const img = document.createElement("img");
        img.src = `data:image/gif;base64,${base64Gif}`;

        // create canvas
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject("Canvas not supported");

        let lastFrame: string | null = null;

        img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;

            // Use <img> inside <video-like> playback with requestAnimationFrame
            const gifPlayer = document.createElement("img");
            gifPlayer.src = img.src;

            let rafId: number;

            const draw = () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(gifPlayer, 0, 0);
                lastFrame = canvas.toDataURL("image/png");
                rafId = requestAnimationFrame(draw);
            };

            gifPlayer.onload = () => {
                draw();
                // wait a bit to ensure GIF plays fully, then grab last frame
                setTimeout(() => {
                    if (rafId) cancelAnimationFrame(rafId);
                    resolve(lastFrame!);
                }, 1500); // adjust based on gif length
            };
        };

        img.onerror = reject;
    });
}



function markdownToPdfmake(md: string): any[] {
    const lines = md.split("\n").filter((l) => l.trim() !== "");
    const blocks: any[] = [];

    lines.forEach((line) => {
        // Headings
        if (/^#{1,6}\s+(.*)/.test(line)) {
            const level = line.match(/^#+/)?.[0].length;
            let text = line.replace(/^#{1,6}\s+/, "").trim();

            // strip inline markdown in headings
            text = text.replace(/[*_`]/g, "");

            blocks.push({
                text,
                style: `h${level}`,
                lineHeight: 1.3,
            });
            return;
        }

        // Detect unordered list item (- or *)
        if (/^[-*]\s+/.test(line)) {
            const text = line.replace(/^[-*]\s+/, "").trim();

            blocks.push({
                ul: [
                    {
                        text: markdownInline(text),
                        style: "listItem",
                        margin: [0, 0, 0, 10],
                        lineHeight: 1.3,
                    }
                ]
            });
            return;
        }

        // Horizontal rule
        if (/^---+$/.test(line)) {
            blocks.push({ canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: "#ccc" }], margin: [0, 2, 10, 2] });
            return;
        }

        // Ordered list (e.g., "1. item")
        if (/^\d+\.\s+/.test(line)) {
            const num = line.match(/^(\d+)\./)?.[1];
            const text = line.replace(/^\d+\.\s+/, "").trim();
            blocks.push({
                columns: [
                    { text: `${num}.`, style: "listNum", width: 15 }, // fixed width
                    { text: markdownInline(text), style: "listItem" }, // parse inline bold/italic
                ],
                columnGap: 2,
                margin: [-5, 10, 0, 10],
            });
            return;
        }


        // Unordered list ("- item")
        if (/^-+\s+/.test(line)) {
            const text = line.replace(/^-+\s+/, "");
            blocks.push({
                columns: [
                    { text: "•", style: "listBullet" },
                    { text, style: "listItem" },
                ],
                columnGap: 4,
                lineHeight: 1.3,
                margin: [0, 4, 0, 4],
            });
            return;
        }

        // Blockquote
        if (/^>\s+(.*)/.test(line)) {
            blocks.push({
                text: line.replace(/^>\s+/, "").trim(),
                style: "blockquote",
                lineHeight: 1.4,
            });
            return;
        }

        // Inline (bold, italic, code)
        const richText: any[] = [];
        const regex = /(\*\*(.*?)\*\*|\*(.*?)\*|`(.*?)`)/g;
        let lastIndex = 0;
        let match: RegExpExecArray | null;

        while ((match = regex.exec(line)) !== null) {
            if (match.index > lastIndex) {
                richText.push({ text: line.substring(lastIndex, match.index) });
            }
            if (match[2]) richText.push({ text: match[2], bold: true });
            else if (match[3]) richText.push({ text: match[3], italics: true });
            else if (match[4]) richText.push({ text: match[4], style: "code" });
            lastIndex = regex.lastIndex;
        }
        if (lastIndex < line.length) {
            richText.push({ text: line.substring(lastIndex) });
        }

        blocks.push({
            text: richText.length > 0 ? richText : line,
            style: "p",
            lineHeight: 1.4,
        });
    });

    return blocks;
}

const pdfStyles = {
    p: { margin: [0, 0, 0, 6], color: "#5f6465", fontSize: 11, lineHeight: 1.4 },
    h1: { fontSize: 14, bold: true, margin: [0, 10, 0, 6], alignment: "left", lineHeight: 1.3 },
    h2: { fontSize: 14, bold: true, margin: [0, 8, 0, 4], alignment: "left", lineHeight: 1.3 },
    h3: { fontSize: 14, bold: true, margin: [0, 6, 0, 3], alignment: "left", lineHeight: 1.3 },
    h4: { fontSize: 14, bold: true, margin: [0, 10, 0, 3], lineHeight: 1.3 },
    blockquote: {
        italics: true,
        margin: [10, 6, 0, 6],
        color: "#666",
        border: [true, false, false, false],
        borderColor: "#ccc",
        lineHeight: 1.4,
    },
    code: {
        fontSize: 10,
        font: "Courier",
        color: "#d63384",
        background: "#f4f4f4",
        margin: [2, 2, 2, 2],
    },
    listBullet: { fontSize: 11, color: "#373D3F", bold: true }, // lighter bullet
    listNum: { fontSize: 11, color: "#373D3F", alignment: "right", bold: true },
    listItem: { fontSize: 11, bold: true, color: "#373D3F" },
};



function markdownInline(line: string): any[] {
    const rich: any[] = [];
    const regex =
        /(\*\*(.*?)\*\*|\*(.*?)\*|`(.*?)`|\[(.*?)\]\((.*?)\)|\(([^ ]+) - (https?:\/\/[^)]+)\))/g;

    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(line)) !== null) {
        if (match.index > lastIndex) {
            rich.push({ text: line.substring(lastIndex, match.index) });
        }

        if (match[2]) rich.push({ text: match[2], bold: true });
        else if (match[3]) rich.push({ text: match[3], italics: true });
        else if (match[4]) rich.push({ text: match[4], style: "code" });
        else if (match[5] && match[6]) {
            // [text](url)
            rich.push({ text: match[5], link: match[6], color: "blue", decoration: "underline" });
        } else if (match[7] && match[8]) {
            // (domain - url)
            rich.push({ text: match[7], italics: true });
            rich.push({ text: " - " });
            rich.push({ text: match[8], link: match[8], color: "blue", decoration: "underline" });
        }

        lastIndex = regex.lastIndex;
    }
    if (lastIndex < line.length) {
        rich.push({ text: line.substring(lastIndex) });
    }
    return rich;
}



function toDataURL(url: string): Promise<string> {
    return fetch(url)
        .then((res) => res.text())
        .then(
            (blob) => {
                return svgToPngDataUrl2(blob);
            }
        );
}

async function svgToPngDataUrl2(svgContent: string, width = 1200): Promise<string> {
    return new Promise((resolve, reject) => {
        const svg = new Blob([svgContent], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(svg);

        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement("canvas");
            const scale = width / img.width;
            canvas.width = width;
            canvas.height = img.height * scale;
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                reject(new Error("Failed to get canvas context"));
                return;
            }
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            URL.revokeObjectURL(url);
            resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = reject;
        img.src = url;
    });
}


function base64ToUint8Array(base64: string): Uint8Array {
    const binaryString = atob(base64.split(",")[1]); // remove data:image/...;base64,
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
}

function markdownToDocx(md: string): Paragraph[] {
    const tree = unified().use(remarkParse).use(remarkGfm).parse(md);
    const paragraphs: Paragraph[] = [];

    function renderNode(node: any): (TextRun | Paragraph)[] {
        switch (node.type) {
            case "paragraph":
                return [
                    new Paragraph({
                        children: (node as any).children?.flatMap(renderNode) as TextRun[],
                        spacing: { after: 200 },
                    }),
                ];

            case "heading":
                return [
                    new Paragraph({
                        text: (node.children || [])
                            .filter((c: any) => c.type === "text")
                            .map((c: any) => c.value)
                            .join(" "),
                        heading:
                            node.depth === 1
                                ? HeadingLevel.HEADING_1
                                : node.depth === 2
                                    ? HeadingLevel.HEADING_2
                                    : HeadingLevel.HEADING_3,
                        spacing: { after: 300 },
                    }),
                ];

            case "list":
                return node.children?.flatMap((li: any) => renderNode(li)) || [];

            // case "listItem":
            //     return [
            //         new Paragraph({
            //             text: liText(node),
            //             bullet: { level: 0 },
            //         }),
            //     ];

            case "strong":
                return [new TextRun({ text: textValue(node), bold: true })];

            case "emphasis":
                return [new TextRun({ text: textValue(node), italics: true })];

            case "inlineCode":
                return [
                    new TextRun({
                        text: node.value as string,
                        font: "Courier New",
                        color: "d63384",
                    }),
                ];

            case "link":
                return [
                    new TextRun({
                        text: node.children?.[0]?.value || node.url || "",
                        style: "Hyperlink",
                    }),
                ];

            case "text":
                return [new TextRun(node.value as string)];

            default:
                return [];
        }
    }

    function textValue(node: any): string {
        if (!node.children) return "";
        return node.children.map((c: any) => c.value || "").join("");
    }

    function liText(node: any): string {
        return node.children
            .map((c: any) =>
                c.type === "paragraph" ? textValue(c) : c.value || ""
            )
            .join(" ");
    }

    tree.children.forEach((child: any) => {
        const rendered = renderNode(child);
        paragraphs.push(
            ...(rendered.filter(
                (n): n is Paragraph => n instanceof Paragraph
            ) as Paragraph[])
        );
    });

    return paragraphs;
}

export async function svgToPngBytes(svg: string, width = 864, height = 612): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const svgBlob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(svgBlob);

        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d")!;
            ctx.drawImage(img, 0, 0, width, height);
            URL.revokeObjectURL(url);

            canvas.toBlob((blob) => {
                if (!blob) return reject("Failed to create PNG blob");
                blob.arrayBuffer().then((buf) => resolve(new Uint8Array(buf)));
            }, "image/png");
        };

        img.onerror = reject;
        img.src = url;
    });
}
